import { useEffect, useMemo, useState } from 'react';
import { submitAppointment, listPublicAbsences, ApiError, type Absence } from '@/lib/api';
import {
  isSunday,
  isUnavailableDate,
  slotsForDate,
  formatAbsencesHint,
  BOOKING_SLOTS_WEEKDAY,
  type AbsenceRange,
} from '@/constants';

const SERVICES = [
  'Séance avec 3 zones',
  'Protocole Complet 5 zones',
  'Ventouse Récupération (Sport)',
  'Forfait Plusieurs Séances',
];

const EMPTY = {
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  service: SERVICES[0],
  date: '',
  heure: BOOKING_SLOTS_WEEKDAY[0],
  message: '',
};

export default function BookingForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [absences, setAbsences] = useState<AbsenceRange[]>([]);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    let cancelled = false;
    listPublicAbsences()
      .then((rows: Absence[]) => {
        if (cancelled) return;
        setAbsences(
          rows.map((r) => ({
            id: r.id,
            from: r.from,
            to: r.to,
            label: r.label,
          })),
        );
      })
      .catch(() => {
        // Form still works; API re-checks absences on submit.
        if (!cancelled) setAbsences([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const availableSlots = useMemo(() => {
    if (!form.date) return BOOKING_SLOTS_WEEKDAY;
    return slotsForDate(form.date, absences);
  }, [form.date, absences]);

  const absencesHint = useMemo(() => formatAbsencesHint(absences), [absences]);

  // After absences load, clear/fix hour if the selected date is closed.
  useEffect(() => {
    if (!form.date) return;
    const slots = slotsForDate(form.date, absences);
    if (slots.length === 0) {
      if (form.heure !== '') setForm((prev) => ({ ...prev, heure: '' }));
    } else if (form.heure && !slots.includes(form.heure)) {
      setForm((prev) => ({ ...prev, heure: slots[0] }));
    }
  }, [absences, form.date, form.heure]);

  const update = (field: keyof typeof EMPTY, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleDateChange = (value: string) => {
    const slots = slotsForDate(value, absences);
    // No slots → clear hour (closed / absence / Sunday)
    const nextHeure = slots.length === 0 ? '' : slots.includes(form.heure) ? form.heure : slots[0];
    setForm((prev) => ({ ...prev, date: value, heure: nextHeure }));
    setErrors([]);
  };

  const noSlotsForDate = Boolean(form.date) && availableSlots.length === 0;
  const dateIsAbsence = form.date ? isUnavailableDate(form.date, absences) : false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    if (form.date && isUnavailableDate(form.date, absences)) {
      setErrors(["Cette date n'est pas disponible (absence). Merci d'en choisir une autre."]);
      return;
    }
    if (form.date && isSunday(form.date)) {
      setErrors(['Aucun créneau ce jour-là. Merci de choisir un autre jour.']);
      return;
    }
    const slots = slotsForDate(form.date, absences);
    if (!form.heure || !slots.includes(form.heure)) {
      setErrors(['Veuillez choisir une heure disponible pour cette date.']);
      return;
    }

    setSubmitting(true);

    try {
      await submitAppointment({
        ...form,
        message: form.message.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.errors?.length ? err.errors : [err.message]);
      } else {
        setErrors(['Envoi impossible. Réessayez ou appelez-nous.']);
      }
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-[#e8e2d4] rounded-[18px] p-8 text-center shadow-sm">
        <div className="w-16 h-16 flex items-center justify-center bg-[#0b6b52] rounded-full mb-6 mx-auto">
          <i className="ri-check-line text-white text-3xl"></i>
        </div>
        <h3
          className="text-[#173029] font-bold text-2xl mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Demande envoyée
        </h3>
        <p className="text-[#57534a] text-sm leading-relaxed mb-6">
          Merci ! Nous avons bien reçu votre demande. Vous recevrez un e-mail dès que votre créneau
          sera validé (sous 24h en jours ouvrés).
        </p>
        <button
          onClick={() => {
            setForm(EMPTY);
            setDone(false);
            setSubmitting(false);
          }}
          className="text-[#0b6b52] hover:text-[#08553f] text-sm font-medium transition-colors"
        >
          Faire une autre demande
        </button>
      </div>
    );
  }

  const inputClass =
    'w-full px-3 py-2.5 rounded-xl bg-[#f6f3ec] border border-[#e8e2d4] text-[#22201c] text-sm placeholder-[#8a8578]/70 focus:outline-none focus:ring-2 focus:ring-[#0b6b52]/40 focus:border-[#0b6b52]';
  const labelClass = 'block text-[#57534a] text-xs font-medium mb-1.5';

  return (
    <div className="bg-white border border-[#e8e2d4] rounded-[18px] p-6 sm:p-8 shadow-sm">
      <h3
        className="text-[#173029] font-bold text-2xl mb-2"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        Demander un rendez-vous
      </h3>
      <p className="text-[#8a8578] text-sm mb-6">
        Demande de créneau — confirmation manuelle par e-mail sous 24h.
      </p>

      {errors.length > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          <ul className="list-disc list-inside space-y-0.5">
            {errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="prenom" className={labelClass}>
              Prénom
            </label>
            <input
              id="prenom"
              required
              maxLength={80}
              value={form.prenom}
              onChange={(e) => update('prenom', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="nom" className={labelClass}>
              Nom
            </label>
            <input
              id="nom"
              required
              maxLength={80}
              value={form.nom}
              onChange={(e) => update('nom', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className={labelClass}>
              E-mail
            </label>
            <input
              id="email"
              type="email"
              required
              maxLength={160}
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="telephone" className={labelClass}>
              Téléphone
            </label>
            <input
              id="telephone"
              type="tel"
              required
              maxLength={30}
              value={form.telephone}
              onChange={(e) => update('telephone', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="service" className={labelClass}>
            Prestation
          </label>
          <select
            id="service"
            required
            value={form.service}
            onChange={(e) => update('service', e.target.value)}
            className={inputClass}
          >
            {SERVICES.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="date" className={labelClass}>
              Date souhaitée
            </label>
            <input
              id="date"
              type="date"
              required
              min={today}
              value={form.date}
              onChange={(e) => handleDateChange(e.target.value)}
              className={inputClass}
            />
            {absencesHint && (
              <p
                className={`mt-1.5 text-xs ${
                  dateIsAbsence ? 'text-red-600 font-medium' : 'text-[#8a8578]'
                }`}
              >
                {absencesHint}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="heure" className={labelClass}>
              Heure souhaitée
            </label>
            <select
              id="heure"
              required={!noSlotsForDate}
              value={form.heure}
              onChange={(e) => update('heure', e.target.value)}
              className={`${inputClass} ${noSlotsForDate ? 'text-red-600 border-red-200 bg-red-50/50' : ''}`}
              disabled={noSlotsForDate}
            >
              {noSlotsForDate ? (
                <option value="">Praticien absent</option>
              ) : (
                availableSlots.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="message" className={labelClass}>
            Message <span className="text-[#8a8578]">(facultatif)</span>
          </label>
          <textarea
            id="message"
            rows={3}
            maxLength={2000}
            value={form.message}
            onChange={(e) => update('message', e.target.value)}
            placeholder="Zones concernées, douleurs, questions…"
            className={`${inputClass} resize-none`}
          />
        </div>

        <button
          type="submit"
          disabled={submitting || noSlotsForDate || !form.heure}
          className="w-full bg-[#0b6b52] hover:bg-[#0d7f61] text-white font-bold py-3.5 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Envoi…' : 'Envoyer ma demande'}
        </button>

        <p className="text-[#8a8578] text-xs text-center">
          Vos données servent uniquement à traiter votre demande de rendez-vous.
        </p>
      </form>
    </div>
  );
}
